import {isValidPhoneNumber} from 'src/utilities/phoneUtils';
import {z} from 'zod';
import {normalizeEmail} from 'src/utilities/extensions/stringExtensions';
import type {FormFieldTypeV2, IFormFieldV2, ValidationTypeV2} from './FormV2';
import {RICHTEXT_MAX_LENGTH_DEFAULT, TEXT_MAX_LENGTH_DEFAULT} from './FormV2';

interface ValidationPattern {
  validator: (schema: z.ZodString) => z.ZodString;
}

/**
 * Strict character set for account/system fields (names, etc.) — Latin letters
 * (incl. diacritics), digits, spaces, hyphens, and apostrophes only. Blocks
 * Asian-language characters and emoji to keep these values clean for
 * downstream-system compatibility. Backend equivalent: AllowStrictCharactersPattern.
 */
const ALLOW_STRICT_CHARACTERS_PATTERN = /^[a-zA-ZÀ-ÿ0-9\s\-']+$/;

/**
 * Operational character set for the majority of fields (task subjects,
 * departments, training titles, etc.) — any-language letters/marks/numbers/
 * punctuation plus standard symbols, while still excluding emoji and control
 * characters. Backend equivalent: AllowOperationalCharactersPattern.
 */
const ALLOW_OPERATIONAL_CHARACTERS_PATTERN =
  /^[\p{L}\p{M}\p{N}\p{P}\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/u;

const validationPatterns: Record<ValidationTypeV2, ValidationPattern> = {
  name: {
    validator: schema =>
      schema.regex(
        ALLOW_STRICT_CHARACTERS_PATTERN,
        'Please enter a valid name using only letters, spaces, hyphens, and apostrophes (e.g., "John O\'Connor" or "Mary-Jane Smith")'
      ),
  },
  email: {
    validator: schema =>
      schema.email(
        'Please enter a valid email address (e.g., "user@example.com")'
      ),
  },
  address: {
    validator: schema =>
      schema.regex(
        /^[a-zA-Z0-9\s,.-]+$/,
        'Please enter a valid address using letters, numbers, spaces, commas, periods, and hyphens (e.g., "123 Main St., Apt 4B")'
      ),
  },
  url: {
    validator: schema =>
      schema.url(
        'Please enter a valid URL starting with http:// or https:// (e.g., "https://www.example.com")'
      ),
  },
  percent: {
    validator: schema =>
      schema.regex(
        /^\d*\.?\d*%?$/,
        'Please enter a valid percentage number with optional decimal and % symbol (e.g., "85%" or "85.5%")'
      ),
  },
  currency: {
    validator: schema =>
      schema.regex(
        /^\$?\d+(\.\d{2})?$/,
        'Please enter a valid currency amount with optional $ symbol and exactly 2 decimal places (e.g., "$10.99" or "10.99")'
      ),
  },
  decimal: {
    validator: schema =>
      schema.regex(
        /^\d*\.?\d*$/,
        'Please enter a valid decimal number (e.g., "123" or "123.45")'
      ),
  },
  time: {
    validator: schema =>
      schema.regex(
        /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
        'Please enter a valid time in 24-hour format using HH:MM (e.g., "09:30" or "14:45")'
      ),
  },
  safe: {
    validator: schema =>
      schema.regex(
        ALLOW_OPERATIONAL_CHARACTERS_PATTERN,
        'Please enter only standard letters, numbers, and symbols (any language supported)'
      ),
  },
};

// Helper function to generate Zod schema based on field configuration
export function generateFieldSchema(field: IFormFieldV2): z.ZodTypeAny {
  let schema:
    | z.ZodString
    | z.ZodNumber
    | z.ZodDate
    | z.ZodBoolean
    | z.ZodEnum<[string, ...string[]]>
    | z.ZodArray<z.ZodTypeAny>
    | z.ZodAny
    | z.ZodEffects<z.ZodTypeAny>
    | z.ZodNullable<z.ZodTypeAny>
    | z.ZodOptional<z.ZodTypeAny>
    | z.ZodUnion<[z.ZodTypeAny, ...z.ZodTypeAny[]]>;

  switch (field.type) {
    case 'text':
    case 'textarea': {
      schema = z.string();

      if (field.required) {
        schema = schema.min(1, `${field.label} is required`);
      }

      const validationType = field.textboxOptions?.validationType ?? 'safe';
      if (validationType) {
        const pattern = validationPatterns[validationType];
        schema = pattern.validator(schema);
      }

      // Apply other validation rules
      if (field.validation?.minLength) {
        schema = schema.min(
          field.validation.minLength,
          `Minimum length is ${field.validation.minLength}`
        );
      }

      const maxLength = field.validation?.maxLength ?? TEXT_MAX_LENGTH_DEFAULT;
      schema = schema.max(maxLength, `Maximum length is ${maxLength}`);

      if (field.validation?.pattern) {
        schema = schema.regex(
          new RegExp(field.validation.pattern),
          field.validation.patternError ||
            field.validation.customError ||
            'Invalid format'
        );
      }

      if (!field.required) {
        // Empty means "not provided" for an optional field — it must not be
        // run through format/length validators (form state holds '' rather
        // than undefined to keep inputs controlled).
        schema = z.literal('').or(schema).nullish();
      }

      if (validationType === 'email') {
        schema = z.preprocess(
          v => (typeof v === 'string' ? normalizeEmail(v) : v),
          schema
        );
      }
      break;
    }

    case 'richtext': {
      schema = z.string();
      if (field.required) {
        schema = schema.trim().nonempty(`${field.label} is required`);
      }
      const richTextMaxLength =
        field.validation?.maxLength ?? RICHTEXT_MAX_LENGTH_DEFAULT;
      schema = schema.max(
        richTextMaxLength,
        `Maximum length is ${richTextMaxLength}`
      );
      break;
    }

    case 'signature':
    case 'drawing-pad':
      schema = z.string();
      if (field.required) {
        schema = schema.trim().nonempty(`${field.label} is required`);
      }
      break;

    case 'phone':
      schema = z.string().optional();
      if (field.required) {
        schema = schema.refine(val => !val || isValidPhoneNumber(val), {
          message: 'Please enter a valid phone number',
        });
      } else {
        schema = schema
          .refine(val => !val || isValidPhoneNumber(val), {
            message: 'Please enter a valid phone number',
          })
          .optional();
      }
      break;

    case 'number': {
      schema = z.preprocess(
        val => (val === undefined || val === '' ? null : Number(val)),
        z.number().nullable()
      );

      if (field.validation?.min) {
        schema = schema.refine(
          val => val === null || val >= field.validation!.min!,
          field.validation.customError ||
            `Minimum value is ${field.validation.min}`
        );
      }
      if (field.validation?.max) {
        schema = schema.refine(
          val => val === null || val <= field.validation!.max!,
          field.validation.customError ||
            `Maximum value is ${field.validation.max}`
        );
      }
      if (field.validation?.excludeValues?.length) {
        schema = schema.refine(
          val =>
            val === null || !field.validation!.excludeValues!.includes(val),
          field.validation.customError ||
            `${field.label} may not be ${field.validation.excludeValues.join(
              ', '
            )}`
        );
      }

      if (field.required) {
        schema = schema.refine(val => val !== null, {
          message: `${field.label} is required`,
        });
      }
      break;
    }
    case 'scale': {
      schema = z.string();
      break;
    }

    case 'select':
    case 'autocomplete':
      if (!field.options || field.options.length === 0) {
        schema = z.preprocess(
          val => (val === undefined || val === '' ? null : val),
          z.union([z.string(), z.number()]).nullable()
        );
        if (field.required) {
          schema = schema.refine(val => val !== null, {
            message: `${field.label} is required`,
          });
        }
      } else {
        // Create a union of possible values including both string and number types
        const possibleValues = field.options
          .map(opt =>
            typeof opt.id === 'number'
              ? [String(opt.id), opt.id]
              : [String(opt.id)]
          )
          .flat();

        schema = z.preprocess(
          val => (val === undefined || val === '' ? null : val),
          z
            .union([
              z.enum([possibleValues[0], ...possibleValues.slice(1)] as [
                string,
                ...string[]
              ]),
              z.number().refine(val => possibleValues.includes(String(val))),
            ])
            .nullable()
        );

        if (field.required) {
          schema = schema.refine(val => val !== null, {
            message: `${field.label} is required`,
          });
        }
      }
      if (field.validation?.excludeValues?.length) {
        const excluded = field.validation.excludeValues.map(String);
        schema = schema.refine(
          val => val === null || !excluded.includes(String(val)),
          field.validation.customError ||
            `${field.label} may not be ${excluded.join(', ')}`
        );
      }
      break;

    case 'select-multiple':
      schema = z.union([z.literal(''), z.array(z.any())]);
      if (field.required) {
        schema = schema.refine(
          val => (Array.isArray(val) && val.length > 0) || val === '',
          {
            message: `${field.label} is required`,
          }
        );
      }
      break;

    case 'asset':
      schema = z.any().nullable();

      if (field.required) {
        schema = schema.refine(val => val !== null, {
          message: `${field.label} is required`,
        });
      }
      break;

    case 'person':
      schema = z.any().nullable();

      if (field.required) {
        schema = schema.refine(
          val => val !== null && val !== undefined && val !== '',
          {
            message: `${field.label} is required`,
          }
        );
      }
      break;

    case 'project':
      schema = z.any().nullable();
      break;

    case 'date':
      // Accept either a Date object, ISO string, or undefined/null
      schema = z.preprocess(val => {
        if (val === undefined || val === '' || val === null) return null;
        if (val instanceof Date) return val;
        // Try to parse string to date
        const parsed = new Date(val as string);
        return isNaN(parsed.getTime()) ? null : parsed;
      }, z.date().nullable());

      if (field.required) {
        schema = schema.refine(val => val !== null, {
          message: `${field.label} is required`,
        });
      }
      break;

    case 'switch':
      schema = z.preprocess(
        val =>
          val === undefined || val === ''
            ? null
            : val === true || val === 'true' || val === 1 || val === '1',
        z.boolean()
      );
      break;

    case 'file': {
      // Helper function to parse file value into array
      const parseFileValue = (val: any): {success: boolean; files: any[]} => {
        if (!val) return {success: true, files: []};
        try {
          const obj = typeof val === 'string' ? JSON.parse(val) : val;
          const arr = Array.isArray(obj) ? obj : [obj];
          return {success: true, files: arr};
        } catch {
          return {success: false, files: []};
        }
      };

      schema = z.string().nullish();

      if (field.required) {
        schema = schema.refine(val => {
          const result = parseFileValue(val);
          return result.success && result.files.length > 0;
        }, `${field.label} is required`);
      }

      if (field.fileUploadOptions?.allowMultiple === false) {
        schema = schema.refine(val => {
          const result = parseFileValue(val);
          return result.success && result.files.length <= 1;
        }, 'Only one file may be uploaded');
      }

      break;
    }

    case 'paragraph':
    case 'heading':
    case 'divider':
      schema = z.any();
      break;

    case 'measurement': {
      const units = field.measurementOptions?.units ?? [];
      const minValue = field.measurementOptions?.minValue;
      const maxValue = field.measurementOptions?.maxValue;
      const decimalPlaces = field.measurementOptions?.decimalPlaces;

      schema = z.preprocess(
        val => {
          if (val === undefined || val === null || val === '') return null;
          const magnitude = (val as {magnitude?: unknown}).magnitude;
          const unit = (val as {unit?: unknown}).unit;
          if (
            magnitude === undefined ||
            magnitude === '' ||
            magnitude === null
          ) {
            return null;
          }
          return {magnitude: Number(magnitude), unit};
        },
        z
          .object({
            magnitude: z.number(),
            unit: z.string().optional(),
          })
          .nullable()
      );

      schema = schema.refine(
        val =>
          val === null || minValue === undefined || val.magnitude >= minValue,
        `Minimum value is ${minValue}`
      );
      schema = schema.refine(
        val =>
          val === null || maxValue === undefined || val.magnitude <= maxValue,
        `Maximum value is ${maxValue}`
      );
      schema = schema.refine(val => {
        if (val === null || decimalPlaces === undefined) return true;
        const factor = 10 ** decimalPlaces;
        return Math.round(val.magnitude * factor) / factor === val.magnitude;
      }, `Maximum ${decimalPlaces} decimal place${decimalPlaces === 1 ? '' : 's'} allowed`);
      schema = schema.refine(
        val =>
          val === null ||
          units.length === 0 ||
          (val.unit !== undefined && units.includes(val.unit)),
        `${field.label} must specify a valid unit`
      );

      if (field.required) {
        schema = schema.refine(val => val !== null, {
          message: `${field.label} is required`,
        });
      }
      break;
    }

    case 'person-select-multiple':
      schema = z.any();
      if (field.required) {
        schema = schema.refine(
          val => {
            return (
              val?.selectAllEntitiesType ||
              (val?.entities && val.entities.length > 0)
            );
          },
          {
            message: `${field.label} is required`,
          }
        );
      }
      break;

    case 'tags':
      schema = z.array(z.string());
      if (field.required) {
        schema = schema.refine(val => Array.isArray(val) && val.length > 0, {
          message: `${field.label} is required`,
        });
      }
      break;

    default:
      schema = z.any();
  }

  // Optional fields must tolerate values that were never seeded — undefined
  // is as valid as null for anything that is not required.
  return field.required ? schema : schema.nullish();
}

// Helper function to generate Zod schema for entire form
export function generateFormSchema(fields: IFormFieldV2[]): z.ZodObject<any> {
  const shape = fields.reduce((acc, field) => {
    acc[field.name] = generateFieldSchema(field);
    return acc;
  }, {} as {[key: string]: z.ZodTypeAny});

  return z.object(shape);
}

/**
 * Checks if a field is filterable based on its type
 * @param field The field to check
 * @returns True if the field is filterable, false otherwise
 */
export function isFilterableField(field: IFormFieldV2): boolean {
  const filterableTypes: FormFieldTypeV2[] = [
    'text',
    'textarea',
    'select',
    'autocomplete',
    'date',
    'number',
    'phone',
    'select-multiple',
    'scale',
    'switch',
    'person',
    'asset',
  ];

  return filterableTypes.includes(field.type);
}
